/**
 * Roster page: CorpDash people as a table. Hours are this calendar month, not the rolling window.
 */
import { useState } from "preact/hooks";
import { Link } from "../components/link.js";
import { Picker } from "../components/picker.js";
import { fmtDayFull, plural, type RosterPageView } from "@trellis/core";

export interface LinkedAccount { integrationId: string; externalId: string; email?: string | null; displayName?: string | null; seenCount?: number; lastSeen?: string }
export interface MappedAccount { integrationId: string; externalId: string; upn: string; displayName?: string | null; mappedBy?: string }

export interface AccountsPanelProps {
  programId: string;
  unmapped: LinkedAccount[];
  mapped: MappedAccount[];
  roster: { upn: string; displayName: string }[];
  /** Whether this viewer may say who an account belongs to. Seeing the list is the looser right. */
  canName: boolean;
}

/** A person's page on this contract, carrying the account a lead arrived from. */
export function personHref(programId: string, upn: string, account?: string): string {
  const at = `/app/programs/${encodeURIComponent(programId)}/roster/${encodeURIComponent(upn)}`;
  return account ? `${at}?account=${encodeURIComponent(account)}` : at;
}

/**
 * Accounts on a linked instance that Trellis could not place. The same person has a
 * different name and address on a customer's instance, so a lead says who they are;
 * Trellis never guesses (docs/2026-09-22-instance-identity.md).
 *
 * This is how anybody finds out a person needs naming at all, so the list stays. The naming itself
 * happens on that person's page, beside everything else they answer to, rather than here as well
 * (docs/2026-09-27-person-identity-page.md).
 */
export function AccountsPanel({ programId, unmapped, mapped, roster, canName }: AccountsPanelProps) {
  const [choice, setChoice] = useState<Record<string, string>>({});
  const keyOf = (a: { integrationId: string; externalId: string }) => `${a.integrationId}:${a.externalId}`;
  const nameOf = (upn: string) => roster.find((r) => r.upn === upn)?.displayName ?? upn;
  if (unmapped.length === 0 && mapped.length === 0) return null;
  return (
    <section class="panel" style="margin-top:16px">
      <div class="panel-head"><span class="label">Accounts on linked systems</span></div>
      {unmapped.length === 0 && <div class="line"><span class="dot good" /><span class="body muted">Every account seen is on somebody's record.</span></div>}
      {unmapped.map((a) => {
        const k = keyOf(a);
        const who = choice[k];
        const named = a.displayName ?? a.externalId;
        return (
          <div class="line" key={k}>
            <span class="dot off" />
            <span class="body">{named}
              <div class="s">{[a.externalId, a.email, a.seenCount ? `seen ${a.seenCount}×` : ""].filter(Boolean).join(" · ")}</div>
              {canName && (
                <div class="inline-form">
                  <Picker id={`who-${k}`} label={`Who is ${named}?`} showLabel placeholder="Search this contract's roster…"
                    options={roster.map((r) => ({ value: r.upn, label: r.displayName, hint: r.upn }))}
                    value={who ?? ""} onChange={(v) => setChoice({ ...choice, [k]: v })} clearable />
                  <div class="buttons">
                    {who
                      ? <Link class="btn" href={personHref(programId, who, k)}>Open<span class="sr-only"> {nameOf(who)} to name this account</span></Link>
                      : <button class="btn" disabled>Open</button>}
                  </div>
                </div>
              )}
            </span>
          </div>
        );
      })}
      {mapped.length > 0 && (
        <details class="fold" style="margin-top:8px">
          <summary class="s">{plural(mapped.length, "account", "accounts")} already on a record</summary>
          {mapped.map((m) => (
            <div class="line" key={keyOf(m)}>
              <span class="dot good" />
              <span class="body">{canName ? <Link class="link" href={personHref(programId, m.upn)}>{nameOf(m.upn)}</Link> : nameOf(m.upn)}
                <div class="s">{[m.externalId, m.displayName].filter(Boolean).join(" · ")}</div>
              </span>
            </div>
          ))}
        </details>
      )}
      {!canName && unmapped.length > 0 && (
        <div class="panel-foot">
          Saying who one of these is belongs to the delivery lead, a deputy, or a program admin, because
          it moves that person's work onto their record.
        </div>
      )}
    </section>
  );
}

/**
 * `personHref` is absent for a viewer who may not state an identity, so the name is plain text
 * rather than a link into a page they would be refused.
 */
export function RosterPageScreen({ view, accounts, personHref: hrefOf }: { view: RosterPageView; accounts?: preact.ComponentChildren; personHref?: ((upn: string) => string) | undefined }) {
  // A column nothing fills is a column of dashes. LCAT has no collector today, so it
  // appears only once something writes one.
  const showLcat = view.people.some((p) => p.lcat);
  const unplaced = view.people.filter((p) => p.placement === "—").length;
  const anyHours = view.people.some((p) => p.hoursThisMonth != null);
  return (
    <main class="main program">
      <div class="contract-head">
        <p class="note dim"><a class="link" href={`/app/programs/${encodeURIComponent(view.programId)}`}>{view.programName}</a></p>
        <div class="contract-title">
          <h1 class="h-lg">Roster</h1>
          <p class="note dim code">{view.asOf ? `As of ${fmtDayFull(view.asOf)}` : "As of today"}{view.stale ? " · stale" : ""}</p>
        </div>
        <p class="lede">
          Who billed this contract, from CorpDash time entries, with the unit Trellis has them on.
          {unplaced ? ` ${plural(unplaced, "person", "people")} sit${unplaced === 1 ? "s" : ""} on no workstream.` : " Everybody sits on a workstream."}
          {hrefOf ? " Open a name to see and set the usernames and addresses Trellis knows them by." : ""}
        </p>
        <p class="note dim">{view.sources.join(" · ")} · {view.ruleVersion}</p>
      </div>

      <section class="panel">
        <table class="wide-table roster-table">
          <thead>
            <tr>
              <th>Person</th>
              {showLcat && <th>LCAT</th>}
              <th>Sits on</th>
              <th class="right">Hours this month</th>
            </tr>
          </thead>
          <tbody>
            {view.people.map((p) => (
              <tr key={p.upn}>
                <td data-label="Person">
                  <span class="row-name">{hrefOf ? <Link class="link" href={hrefOf(p.upn)}>{p.name}</Link> : p.name}</span>
                  <span class="note dim">{p.upn}{p.flagged ? " · flagged" : ""}{p.source === "manual" ? " · added by hand" : ""}</span>
                </td>
                {showLcat && <td data-label="LCAT">{p.lcat ?? "—"}</td>}
                <td data-label="Sits on">{p.placement === "—" ? <span class="tone-watch">No workstream</span> : readPlacement(p.placement)}</td>
                <td data-label="Hours this month" class="num right">{p.hoursThisMonth == null ? "—" : Math.round(p.hoursThisMonth)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {view.people.length === 0 && <div class="muted" style="margin-top:12px">Nobody on the roster yet.</div>}
        {(unplaced > 0 || (view.people.length > 0 && !anyHours)) && (
          <div class="panel-foot">
            {unplaced > 0 && <>
              {plural(unplaced, "person", "people")} bill{unplaced === 1 ? "s" : ""} without a workstream. Place them on the{" "}
              <a class="link" href={`/app/programs/${encodeURIComponent(view.programId)}/structure`}>Structure</a>.{" "}
            </>}
            {!anyHours && "No time entries from CorpDash for this calendar month."}
          </div>
        )}
      </section>
      {accounts}
    </main>
  );
}

/**
 * `placementOf` packs a person's units as `workstream:team,team – workstream`, which is a
 * key and not a sentence. Read it back out as the path a person would say.
 */
function readPlacement(text: string): string {
  return text
    .split(" – ")
    .map((part) => {
      const [ws, teams] = part.split(":");
      return teams ? `${ws} › ${teams.split(",").join(", ")}` : ws ?? part;
    })
    .join(" · ");
}
